import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Edit2, User, Save } from "lucide-react";

export default function FavoritePlayers() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [formData, setFormData] = useState({ name: "", handicap: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPlayers();
  }, []);

  const fetchPlayers = async () => {
    try {
      const response = await fetch(`${API}/players/favorites`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setPlayers(data);
      }
    } catch {
      
      toast.error("Error al cargar jugadores");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlayer = async () => {
    if (!formData.name.trim() || formData.handicap === "") {
      toast.error("Completa todos los campos");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/players/favorites`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: formData.name.trim(),
          handicap: parseInt(formData.handicap),
        }),
      });

      if (response.ok) {
        const newPlayer = await response.json();
        setPlayers([...players, newPlayer].sort((a, b) => a.name.localeCompare(b.name)));
        setShowAddDialog(false);
        setFormData({ name: "", handicap: "" });
        toast.success("Jugador agregado");
      } else {
        throw new Error("Error creating player");
      }
    } catch {
      
      toast.error("Error al agregar jugador");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePlayer = async () => {
    if (!formData.name.trim() || formData.handicap === "") {
      toast.error("Completa todos los campos");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/players/favorites/${editingPlayer.player_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: formData.name.trim(),
          handicap: parseInt(formData.handicap),
        }),
      });

      if (response.ok) {
        const updatedPlayer = await response.json();
        setPlayers(
          players
            .map(p => p.player_id === editingPlayer.player_id ? updatedPlayer : p)
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        setEditingPlayer(null);
        setFormData({ name: "", handicap: "" });
        toast.success("Jugador actualizado");
      } else {
        throw new Error("Error updating player");
      }
    } catch {
      
      toast.error("Error al actualizar jugador");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePlayer = async (playerId) => {
    if (!window.confirm("¿Eliminar este jugador de favoritos?")) return;

    try {
      const response = await fetch(`${API}/players/favorites/${playerId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setPlayers(players.filter(p => p.player_id !== playerId));
        toast.success("Jugador eliminado");
      } else {
        throw new Error("Error deleting player");
      }
    } catch {
      
      toast.error("Error al eliminar jugador");
    }
  };

  const openEditDialog = (player) => {
    setEditingPlayer(player);
    setFormData({ name: player.name, handicap: player.handicap.toString() });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[#0F172A]">Jugadores Favoritos</h1>
            <p className="text-xs text-[#64748B]">{players.length} jugadores guardados</p>
          </div>
          <Button
            onClick={() => {
              setFormData({ name: "", handicap: "" });
              setShowAddDialog(true);
            }}
            className="btn-primary"
            data-testid="add-player-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Agregar
          </Button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {players.length === 0 ? (
          <Card className="card-default">
            <CardContent className="p-8 text-center">
              <User className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
              <p className="text-[#64748B] mb-4">
                No tienes jugadores favoritos guardados
              </p>
              <Button
                onClick={() => setShowAddDialog(true)}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar Jugador
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {players.map((player) => (
              <Card key={player.player_id} className="card-default" data-testid={`player-${player.player_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#1B4D3E]/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-[#1B4D3E]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#0F172A]">{player.name}</p>
                        <p className="text-sm text-[#64748B]">Handicap: {player.handicap}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(player)}
                        data-testid={`edit-${player.player_id}`}
                      >
                        <Edit2 className="w-4 h-4 text-[#64748B]" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeletePlayer(player.player_id)}
                        data-testid={`delete-${player.player_id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar Jugador</DialogTitle>
            <DialogDescription>
              Agrega un jugador a tu lista de favoritos
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Nombre</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre del jugador"
                className="mt-1"
                data-testid="player-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Handicap</label>
              <Input
                type="number"
                min="0"
                max="54"
                value={formData.handicap}
                onChange={(e) => setFormData({ ...formData, handicap: e.target.value })}
                placeholder="0-54"
                className="mt-1"
                data-testid="player-handicap-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAddPlayer}
              disabled={submitting}
              className="btn-primary"
              data-testid="save-player-btn"
            >
              {submitting ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingPlayer} onOpenChange={(open) => !open && setEditingPlayer(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Jugador</DialogTitle>
            <DialogDescription>
              Modifica los datos del jugador
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Nombre</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre del jugador"
                className="mt-1"
                data-testid="edit-player-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Handicap</label>
              <Input
                type="number"
                min="0"
                max="54"
                value={formData.handicap}
                onChange={(e) => setFormData({ ...formData, handicap: e.target.value })}
                placeholder="0-54"
                className="mt-1"
                data-testid="edit-player-handicap-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPlayer(null)}>
              Cancelar
            </Button>
            <Button
              onClick={handleUpdatePlayer}
              disabled={submitting}
              className="btn-primary"
              data-testid="update-player-btn"
            >
              {submitting ? "Guardando..." : "Actualizar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
